<script lang="ts">
  // Libs
  import { onDestroy } from "svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";

  // Services
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { shouldHideAllDistributorInfo } from "@shared/services/retailer_user_authentication_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  // Stores
  import CartRecommendationsStore from "@retailer/svelte/stores/CartRecommendationsStore";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { get as getStoreValue } from "svelte/store";

  // Components
  import Header from "@retailer/svelte/Cart/Page/DistributorSection/Header.svelte";
  import Footer from "@retailer/svelte/Cart/Page/DistributorSection/Footer.svelte";
  import Body from "@retailer/svelte/Cart/Page/DistributorSection/Body.svelte";
  import PriceAccordionWrapper from "@retailer/svelte/Cart/Page/DistributorSection/PriceAccordionWrapper.svelte";
  import LoadingSpinner from "@shared/svelte/Components/LoadingSpinner.svelte";
  import RecommendationProductsContainer from "@retailer/svelte/Cart/Page/DistributorSection/RecommendationProductsContainer.svelte";
  import Tabs from "@shared/svelte/Components/Tabs.svelte";

  // Types
  import type { CartOrderType } from "@shared/types/Cart";
  import type { Product } from "@shared/types/ProductLine";

  // Entities
  import { CartOrderEntity } from "@shared/services/event_tracking/entities";
  import { CartRecommendationTabEntity } from "@shared/services/event_tracking/entities";

  // Props
  export let order: CartOrderType;
  export let cartService: any; // Angular Service
  export let customPricingMode: any;
  export let callbacks: any;
  export let showLoading: boolean;
  export let openConnectDistributorModal: any;
  export let isMobile: boolean;
  export let accordionOpenByDefault: boolean;
  export let highlightErrors: boolean;
  export let futurePricesLoading: boolean = true;

  // Data
  let desktopAccordionOpen = accordionOpenByDefault && !isMobile;

  const { trackClick, trackMrcEvents } = EventTracking;

  const onClickAccordion = () => {
    desktopAccordionOpen = !desktopAccordionOpen;

    const eventType = desktopAccordionOpen ? "expand_accordion" : "collapse_accordion";
    trackAccordionInteraction(eventType);
  };

  const trackAccordionInteraction = (eventType: string) => {
    trackClick(
      eventType,
      new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
    );
  };

  const highlightOrderErrorsOnSubmit = () => {
    highlightErrors = true;
  };

  const distributorId: number = order.distributor.id;

  let recommendations: Product[] = [];
  let recommendationsLoading: boolean = true;
  const getRecommendations = () => {
    const storedRecommendations = getStoreValue(CartRecommendationsStore)[distributorId]?.products;
    if (storedRecommendations) {
      recommendations = storedRecommendations;
      recommendationsLoading = false;
      return;
    }

    HttpPromise({
      method: "GET",
      url: "/api/retailer/cart/recommendations",
      data: {
        distributor_id: distributorId,
      },
    })
      .then(res => {
        recommendations = res;
        recommendationsLoading = false;

        CartRecommendationsStore.update(n => {
          const obj = n[distributorId] || { products: [] };
          n[distributorId] = { ...obj, products: res };
          return n;
        });
      })
      .catch(() => {
        recommendations = [];
        recommendationsLoading = false;
      });
  };

  getRecommendations();

  const trackingEntity = [
    new CartRecommendationTabEntity({
      distributor_id: order.distributor.id,
      order_id: order.id,
    }),
  ];

  const trackingHelper = tabName => {
    const targetObject =
      "cart-products-tab" === tabName ? "cart_products_tab" : "cart_recommendations_tab";
    return { trackingEntity, targetObject };
  };

  let tabs;

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  let hideAllDistributorInfo: boolean = false;
  $: {
    tabs = [
      {
        id: "cart-products-tab",
        title: `Items in cart (${order.total_items})`,
        component: Body,
        params: {
          order,
          cartService,
          callbacks,
          showLoading,
          customPricingMode,
        },
      },
    ];
    hideAllDistributorInfo =
      hasRetailerUserAuthRollout &&
      distributorId &&
      shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, [distributorId]);

    let idx = tabs.findIndex(obj => obj.id === "cart-recommendation-products-tab");

    if (hideAllDistributorInfo) {
      if (idx > -1) {
        tabs.splice(idx, 1);
      }
    } else {
      if (idx == -1) {
        tabs.push({
          id: "cart-recommendation-products-tab",
          title: "More to consider",
          component: RecommendationProductsContainer,
          params: {
            recommendations,
            order,
          },
        });
      }
    }
  }

  onDestroy(() => {
    CartRecommendationsStore.update(n => {
      delete n[order.distributor.id];
      return n;
    });
  });
</script>

<style>
  .fizz-border-white {
    border: 2px solid #ffffff;
    border-radius: 4px;
  }

  .active-border {
    border: 2px solid #008fbe;
    border-radius: 4px;
  }
</style>

<div
  class="fizz-full-width fizz-surface fizz-radius-medium"
  class:active-border={desktopAccordionOpen}
  class:fizz-border-white={!desktopAccordionOpen}
  data-testid={"distributor-section-" + order.distributor.id}
>
  <Header
    {isMobile}
    {order}
    {openConnectDistributorModal}
    total={order.total}
    products={order.products}
    onClickAccordionCallback={onClickAccordion}
    {accordionOpenByDefault}
    {cartService}
    {highlightErrors}
  />
  <PriceAccordionWrapper
    {isMobile}
    total={order.total}
    products={order.products}
    {accordionOpenByDefault}
    onClick={trackAccordionInteraction}
  >
    {#if desktopAccordionOpen || isMobile}
      {#key recommendations}
        {#if recommendationsLoading || futurePricesLoading}
          <LoadingSpinner />
        {:else if recommendations.length}
          <div
            use:trackMrcEvents={[trackingEntity]}
            data-testid="distributor-{order.distributor.id}-section-recommendations"
          >
            <Tabs {trackingHelper} {tabs} active={0} />
          </div>
        {:else}
          <Body {order} {cartService} {callbacks} {showLoading} {customPricingMode} />
        {/if}
      {/key}
    {/if}
  </PriceAccordionWrapper>
  {#key order.id}
    <div class:fizz-border-top={!isMobile}>
      <Footer
        {order}
        bind:showLoading
        {cartService}
        on:submitWithErrors={highlightOrderErrorsOnSubmit}
      />
    </div>
  {/key}
</div>
